import { useEffect, useState } from "react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { useToast } from "src/hooks/useToast";
import { supabase } from "src/lib/supabaseClient";
import { nodeFeedbackService, type NodeFeedbackRequest } from "src/services/nodeFeedbackService";

const levelLabel: Record<string, string> = { developing: "Developing", on_track: "On track", secured: "Secured" };

const StudentCurriculumProgress = () => {
  const { toast } = useToast();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const [requests, setRequests] = useState<NodeFeedbackRequest[]>([]);
  const [attemptCount, setAttemptCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTitle("Curriculum progress");
    setDescription("Assessments and tutor notes show progress. They do not block the next lesson.");
    return () => reset();
  }, [reset, setDescription, setTitle]);

  useEffect(() => {
    let current = true;
    Promise.all([
      nodeFeedbackService.listForStudent(),
      supabase.auth.getUser().then(async ({ data: { user } }) => {
        if (!user) return 0;
        const { count, error } = await supabase.from("learner_evidence_events").select("id", { count: "exact", head: true }).eq("student_id", user.id);
        if (error) throw new Error(error.message);
        return count || 0;
      }),
    ]).then(([rows, count]) => {
      if (!current) return;
      setRequests(rows);
      setAttemptCount(count);
    }).catch((error) => {
      if (current) toast({ title: "Unable to load curriculum progress", description: error instanceof Error ? error.message : "Please try again.", type: "error" });
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [toast]);

  return <div className="space-y-4">
    <p className="text-sm text-slate-600">{loading ? "Loading progress…" : `${attemptCount} assessment attempt${attemptCount === 1 ? "" : "s"} recorded.`}</p>
    {!loading && requests.length === 0 && <p className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600">No lesson, unit, or domain feedback has been requested yet. You can continue to the next objective.</p>}
    {requests.map((request) => <article key={request.id} className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{request.nodeType}</p>
      <h2 className="text-lg font-semibold">{request.nodeName}</h2>
      {request.status === "open" ? <p className="mt-2 text-sm text-slate-600">Tutor feedback is not recorded yet.</p> : <div className="mt-2 text-sm text-slate-700"><p className="font-medium">{levelLabel[request.progressLevel || ""] || "Progress recorded"}</p><p className="mt-1">{request.detailedFeedback}</p></div>}
    </article>)}
  </div>;
};

export default StudentCurriculumProgress;
